import { Box, Stack } from "@mui/system";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PageContainer } from "src/components/page-container";
import { usePageView } from "src/hooks/use-page-view";
import { AccountBillingSettings } from "src/sections/dashboard/account/account-billing-settings";
// import { BillingPlans } from "src/sections/pricing/BillingPlans";
import { CurrentSubscriptionCard } from "src/sections/pricing/CurrentSubscriptionCard";
import { ManageWorkspacesCard } from "src/sections/pricing/ManageWorkspacesCard";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useBillingStatus } from "src/hooks/use-billing";
import { useWorkspaces } from "src/hooks/use-workspaces";
import { useOrganization } from "src/contexts/organization/OrganizationContext";
import { useThumbzApi } from "src/services/thumbz-api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getServerErrorArrayMessage } from "src/utils/get-server-error-message";

export const Route = createFileRoute("/_authenticated/organizacao/faturamento/")({
  component: () => <Page />,
});

const Page = () => {
  usePageView();

  const { data: billingData, refetchAllQueries } = useBillingStatus();
  const { data: workspaces = [] } = useWorkspaces();
  const workspaceCount = (workspaces as any[])?.length ?? 0;
  const isFreePlan = String(billingData.plan?.slug).toLowerCase() === "free";

  const { selectedOrganization } = useOrganization();
  const { api } = useThumbzApi();
  const queryClient = useQueryClient();

  const [openInfo, setOpenInfo] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const downgradeMutation = useMutation({
    mutationFn: api.billing.billingControllerDowngradeToFree,
    onSuccess: () => {
      toast.success("Plano alterado para o gratuito com sucesso.");
      // Refresh billing-related caches
      refetchAllQueries();
      queryClient.invalidateQueries({
        predicate: (q) => String(q.queryKey[0]).includes("billing"),
      });
      setOpenConfirm(false);
    },
    onError: (error) => getServerErrorArrayMessage(error).forEach((m) => toast.error(m)),
  });

  return (
    <PageContainer
      title="Faturamento"
      subtitle="Gerencie suas informações de faturamento e visualize suas faturas."
      // leftNode={<AccountBillingStatus force_show />}
    >
      <Stack spacing={4}>
        <CurrentSubscriptionCard />
        {!isFreePlan && <ManageWorkspacesCard />}
        {/* <BillingPlans /> */}
        <AccountBillingSettings />
        {/* <OrganizationUsage type="workspaces" /> */}
        {!isFreePlan && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              size="small"
              color="warning"
              variant="text"
              onClick={() => {
                if (workspaceCount > 1) setOpenInfo(true);
                else setOpenConfirm(true);
              }}
            >
              Downgrade para plano gratuito
            </Button>
          </Box>
        )}
      </Stack>

      {/* Info dialog when user has more than 1 workspace */}
      <Dialog open={openInfo} onClose={() => setOpenInfo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Antes de mudar para o plano gratuito</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Sua organização possui {workspaceCount} workspaces. O plano gratuito permite apenas 1
            workspace. Exclua ou arquive os workspaces excedentes antes de realizar o downgrade.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfo(false)}>Entendi</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog when eligible */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar downgrade</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja mudar para o plano gratuito? Sua assinatura atual será cancelada
            e os recursos serão limitados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
          <Button
            color="warning"
            onClick={() =>
              downgradeMutation.mutate({ org_id: String(selectedOrganization?.org_id) })
            }
            disabled={downgradeMutation.isPending}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
